import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Droplets, Wind, Network, HeartPulse, ShieldCheck, Cpu, Info } from "lucide-react";

function InfoModal({ open, onClose, title, children }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="info-modal">
          <div className="absolute inset-0 bg-black/85 backdrop-blur-xl" onClick={onClose} />
          <motion.div
            initial={{ y: 40, scale: 0.97 }} animate={{ y: 0, scale: 1 }} exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative glass rounded-2xl w-full max-w-3xl max-h-[86vh] overflow-y-auto p-8 sm:p-12"
          >
            <button onClick={onClose} data-testid="info-modal-close" className="sticky top-0 float-right -mt-2 -mr-2 w-10 h-10 rounded-full glass grid place-items-center text-white hover:text-[var(--gold)]">
              <X size={18} />
            </button>
            <h3 className="font-display text-3xl sm:text-4xl gold-fill mb-6 pr-10">{title}</h3>
            <div className="space-y-4 text-white/75 leading-relaxed text-[15px]">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const H = ({ children }) => <div className="overline text-[var(--gold)] mt-6 mb-1">{children}</div>;

function ProtectionCard({ icon: Icon, title, children }) {
  return (
    <div className="glass rounded-xl p-7" data-testid="protection-card">
      <div className="w-11 h-11 rounded-full grid place-items-center border border-[var(--gold)]/40 mb-5">
        <Icon size={18} className="text-[var(--gold)]" />
      </div>
      <h4 className="font-display text-xl text-white mb-3">{title}</h4>
      <p className="text-white/60 text-sm leading-relaxed">{children}</p>
    </div>
  );
}

export default function Offerings() {
  const [modal, setModal] = useState(null); // 'paint' | 'water' | null

  return (
    <section id="offerings" className="relative py-24 lg:py-36 border-t border-white/5" data-testid="offerings-section">
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
        <div className="overline mb-4">What We Do</div>

        {/* PAINTING */}
        <div className="mb-24 lg:mb-32">
          <h2 className="font-display font-light text-4xl sm:text-6xl lg:text-7xl text-white leading-[1.02] mb-6">
            Premium Interior &amp; <span className="gold-fill">Exterior Painting</span>
          </h2>
          <p className="text-white/60 text-lg leading-relaxed max-w-3xl mb-8">
            We power wash your entire exterior. We cover the ground area and any concrete around the work area,
            sand and caulk and repair any damage, then extensively prep, tape and mask. We tent over your plants
            and landscaping before painting — and immediately remove all tape and protective covers once the work
            is complete.
          </p>
          <button onClick={() => setModal("paint")} data-testid="paint-more-info" className="btn-ghost rounded-full px-7 py-3 text-xs tracking-[0.18em] uppercase inline-flex items-center gap-2">
            <Info size={14} /> More Info
          </button>
        </div>

        {/* INTELLIGENT HOME PROTECTION */}
        <div>
          <h2 className="font-display font-light text-4xl sm:text-6xl lg:text-7xl text-white leading-[1.02] mb-4">
            Intelligent <span className="gold-fill">Home Protection</span>
          </h2>
          <p className="text-[var(--gold)] text-lg sm:text-xl tracking-wide mb-4">Water. HVAC. Occupant Safety. One Local Intelligence Platform.</p>
          <p className="text-white/60 text-lg leading-relaxed max-w-3xl mb-12">
            Our system continuously monitors the critical systems inside your home and responds to problems the
            moment they are detected.
          </p>

          {/* Water protection highlight */}
          <div className="glass rounded-2xl p-8 sm:p-10 mb-12 border border-[var(--gold)]/20">
            <div className="flex items-center gap-3 mb-4">
              <Droplets size={20} className="text-[var(--gold)]" />
              <h3 className="font-display text-2xl sm:text-3xl text-white">Water Protection — Response in Milliseconds</h3>
            </div>
            <p className="text-white/70 leading-relaxed max-w-3xl mb-4">
              Our water-monitoring hardware and software are engineered to detect abnormal flow conditions and a
              potential burst pipe within milliseconds of the event. Our winter freeze-damage prevention systems use
              the newest proven technology that attaches to your plumbing at the entry point of your home — ultrasonic
              wave sensory devices that calculate wave differentials 200 times a second.
            </p>
            <button onClick={() => setModal("water")} data-testid="water-more-info" className="btn-gold rounded-full px-7 py-3 text-xs tracking-[0.18em] uppercase inline-flex items-center gap-2">
              <Info size={14} /> More Info
            </button>
          </div>

          {/* Protection feature grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <ProtectionCard icon={Wind} title="HVAC System Monitoring">
              The same intelligent monitoring watches your HVAC equipment continuously and identifies abnormal conditions
              before they become larger failures — helping prevent expensive damage, wasted energy, condensate water damage,
              and costly emergency repairs. Early detection can save high-value homes thousands.
            </ProtectionCard>
            <ProtectionCard icon={Network} title="One Connected Home Platform">
              Every monitoring station can operate independently while joining a larger home intelligence network. Water,
              HVAC, environmental and occupant monitoring all communicate through one system — integrating with compatible
              smart-home platforms like Amazon Alexa, or running on our own Home Health AI.
            </ProtectionCard>
            <ProtectionCard icon={HeartPulse} title="Home Health AI">
              Beyond mechanical systems, the platform can monitor the people inside — body temperature, heart rate, breathing,
              and fall detection. When a configured emergency is detected, it initiates alerts and contacts the people you
              choose in advance, in the order you decide.
            </ProtectionCard>
            <ProtectionCard icon={Cpu} title="Local Intelligence. No Cloud Required.">
              Core protection runs locally and does not need an active cloud connection to monitor or respond. Your most
              important protection keeps working even without internet — with battery-backed, independent response logic
              that stays inside the property.
            </ProtectionCard>
          </div>

          <div className="glass rounded-2xl p-8 sm:p-10 mt-12 border border-[var(--gold)]/20 flex items-start gap-4">
            <ShieldCheck size={26} className="text-[var(--gold)] shrink-0 mt-1" />
            <div>
              <h3 className="font-display text-2xl sm:text-3xl text-white mb-3">Your Home Should React Before You Can.</h3>
              <p className="text-white/70 leading-relaxed max-w-3xl">
                A burst pipe can release a tremendous amount of water before anyone knows. An HVAC failure gets expensive
                long before it's obvious. An occupant emergency can happen when no one else is in the room. Our system is
                built around one idea: the home should detect the problem and begin responding immediately — water protection,
                HVAC intelligence, occupant monitoring, and automated emergency response, in one integrated platform.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* PAINTING MODAL */}
      <InfoModal open={modal === "paint"} onClose={() => setModal(null)} title="Our Painting Process">
        <p>Every Crown Artisan paint project follows a meticulous, protective process — because a flawless finish starts long before the first coat.</p>
        <H>1 · Power Wash</H>
        <p>We power wash your entire exterior to remove dirt, chalking, mildew and loose material so new paint bonds properly.</p>
        <H>2 · Protect the Work Area</H>
        <p>We cover the ground area and any concrete around the work area to keep your property spotless.</p>
        <H>3 · Repair &amp; Prep</H>
        <p>We sand and caulk and repair any damage, then extensively prep, tape and mask every edge and surface.</p>
        <H>4 · Tent the Landscaping</H>
        <p>We tent over your plants and landscaping before painting to shield them completely.</p>
        <H>5 · Paint &amp; Reveal</H>
        <p>After painting, we immediately remove tape and protective covers — leaving a crisp, clean, finished result.</p>
      </InfoModal>

      {/* WATER SYSTEM MODAL */}
      <InfoModal open={modal === "water"} onClose={() => setModal(null)} title="Water Protection — How It Works">
        <p>Once a burst condition is detected, the system automatically initiates an electronic actuator shutoff sequence — without waiting for someone to receive an alert, open an app, or manually close a valve.</p>
        <p>The system incorporates two independent automatic shutoff devices, each operating through a separate device driver. This redundant architecture is designed to provide a complete water shutoff in under three seconds, dramatically reducing the amount of water released after a pipe failure. A dedicated battery-backup system keeps the protection operational during a power outage.</p>
        <p className="text-[var(--gold)] font-display text-xl">Detect. Verify. Isolate. Shut Down.</p>
        <p>The response happens locally and automatically.</p>
        <H>Redundant By Design</H>
        <p>Critical protection should never depend on a single point of failure. Our water-protection architecture uses:</p>
        <ul className="list-disc pl-5 space-y-1 text-white/70">
          <li>Dual independent electronic shutoff valves</li>
          <li>Separate actuator device drivers</li>
          <li>Continuous water-system monitoring</li>
          <li>Millisecond-level event detection</li>
          <li>Automatic shutoff logic</li>
          <li>Battery-backup operation</li>
          <li>Local processing and control</li>
          <li>Continuous system-health monitoring</li>
        </ul>
        <p>If a serious water event occurs, the system is designed to respond immediately — even when no one is home.</p>
        <H>No Internet Required for Primary Protection</H>
        <p>No cloud required for automatic shutoff. Local processing. Battery-backed operation. Independent response architecture.</p>
      </InfoModal>
    </section>
  );
}
